import { LocalizedError } from '@/core/i18n';
import { fetchJson, HttpError } from '@/core/network';
import type { SearchItem, SearchViewport } from '../types';

// Photon (komoot), a geocoder over OpenStreetMap data.
const PHOTON_URL = 'https://photon.komoot.io/api/';

interface PhotonFeature {
  geometry: { coordinates: [number, number] };
  properties: {
    name?: string;
    housenumber?: string;
    street?: string;
    city?: string;
    county?: string;
    state?: string;
    country?: string;
    osm_value?: string;
    type?: string;
    extent?: [number, number, number, number];
  };
}

export interface PhotonQuery {
  q: string;
  limit: number;
  viewport: SearchViewport;
  signal: AbortSignal;
  /** Only towns, cities, … */
  placesOnly?: boolean;
}

function toItem(f: PhotonFeature): SearchItem {
  const p = f.properties;
  const name = p.name || [p.housenumber, p.street].filter(Boolean).join(' ') || p.city || p.country || '';
  const type = (p.osm_value || p.type || '').replace(/_/g, ' ');
  const where = [p.city, p.county, p.state, p.country].filter(
    (v, i, a) => v && v !== name && a.indexOf(v) === i,
  );
  const [lng, lat] = f.geometry.coordinates;
  const text = [type && type[0].toUpperCase() + type.slice(1), where.join(', ')].filter(Boolean).join(' · ');
  return { name, lat, lng, extent: p.extent, details: { kind: 'place', text } };
}

export async function photonSearch({
  q,
  limit,
  viewport,
  signal,
  placesOnly,
}: PhotonQuery): Promise<SearchItem[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  if (placesOnly) params.set('osm_tag', 'place');
  params.set('lat', viewport.lat.toFixed(4));
  params.set('lon', viewport.lng.toFixed(4));
  params.set('zoom', String(Math.round(viewport.zoom)));
  params.set('location_bias_scale', '0.3');
  try {
    const res = await fetchJson<{ features: PhotonFeature[] }>(`${PHOTON_URL}?${params}`, signal);
    return res.features.map(toItem);
  } catch (err) {
    if (err instanceof HttpError)
      throw new LocalizedError('search:errors.service', { status: err.status }, { cause: err });
    throw err;
  }
}
